import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import mammoth from "mammoth";

export interface CloudConnection {
  id: string;
  provider: "google" | "dropbox";
  provider_email: string | null;
  created_at: string;
}

export interface CloudFile {
  id: string;
  name: string;
  path?: string;
  isFolder?: boolean;
  modifiedTime?: string;
  mimeType?: string;
}

export function useCloudConnections() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: connections = [],
    isLoading: loading,
    refetch: refresh,
  } = useQuery({
    queryKey: ["user_connections", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_connections")
        .select("id, provider, provider_email, created_at")
        .eq("user_id", user.id);

      if (error) throw error;
      return data as CloudConnection[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  const getConnection = (provider: "google" | "dropbox") => {
    return connections.find((c) => c.provider === provider);
  };

  const isConnected = (provider: "google" | "dropbox") => {
    return !!getConnection(provider);
  };

  const connectGoogle = () => {
    if (!user) return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast({
        title: "Configuration Error",
        description: "Google Client ID is not configured",
        variant: "destructive",
      });
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const redirectUri = `${supabaseUrl}/functions/v1/google-oauth-callback`;
    const scope = encodeURIComponent(
      "https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email"
    );
    const state = encodeURIComponent(`${window.location.origin}${window.location.pathname}|${user.id}`);

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scope}&access_type=offline&prompt=consent&state=${state}`;

    window.location.href = authUrl;
  };

  const connectDropbox = () => {
    if (!user) return;

    const appKey = import.meta.env.VITE_DROPBOX_APP_KEY;
    if (!appKey) {
      toast({
        title: "Configuration Error",
        description: "Dropbox App Key is not configured",
        variant: "destructive",
      });
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const redirectUri = `${supabaseUrl}/functions/v1/dropbox-oauth-callback`;
    const state = encodeURIComponent(`${window.location.origin}${window.location.pathname}|${user.id}`);

    const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${appKey}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=${state}`;

    window.location.href = authUrl;
  };

  const disconnect = async (provider: "google" | "dropbox") => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_connections")
        .delete()
        .eq("user_id", user.id)
        .eq("provider", provider);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["user_connections", user.id] });
      toast({
        title: "Disconnected",
        description: `Successfully disconnected from ${provider === "google" ? "Google Drive" : "Dropbox"}`,
      });
    } catch (error) {
      console.error("Error disconnecting:", error);
      toast({
        title: "Error",
        description: "Failed to disconnect",
        variant: "destructive",
      });
    }
  };

  return {
    connections,
    loading,
    isConnected,
    getConnection,
    connectGoogle,
    connectDropbox,
    disconnect,
    refresh,
  };
}

export function useGoogleDriveFiles() {
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listFiles = useCallback(async (query?: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke("google-drive-files", {
        body: { action: "list", query },
      });

      if (error) throw error;
      if (data.error) {
        if (data.code === "NOT_CONNECTED") {
          setError("NOT_CONNECTED");
        } else {
          throw new Error(data.error);
        }
        return;
      }

      setFiles(data.files || []);
    } catch (err) {
      console.error("Error listing Google Drive files:", err);
      setError(err instanceof Error ? err.message : "Failed to list files");
    } finally {
      setLoading(false);
    }
  }, []);

  const importFile = useCallback(async (fileId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("google-drive-files", {
        body: { action: "import", fileId },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data.content;
    } catch (err) {
      console.error("Error importing Google Drive file:", err);
      throw err;
    }
  }, []);

  return { files, loading, error, listFiles, importFile };
}

export function useDropboxFiles() {
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState("");

  const listFiles = useCallback(async (path?: string, query?: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke("dropbox-files", {
        body: { action: "list", path: path || "", query },
      });

      if (error) throw error;
      if (data.error) {
        if (data.code === "NOT_CONNECTED") {
          setError("NOT_CONNECTED");
        } else {
          throw new Error(data.error);
        }
        return;
      }

      setFiles(data.files || []);
      if (path !== undefined) {
        setCurrentPath(path);
      }
    } catch (err) {
      console.error("Error listing Dropbox files:", err);
      setError(err instanceof Error ? err.message : "Failed to list files");
    } finally {
      setLoading(false);
    }
  }, []);

  const importFile = useCallback(async (path: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("dropbox-files", {
        body: { action: "import", path },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Handle .docx files
      if (data.isDocx) {
        const binaryStr = atob(data.content);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        const result = await mammoth.extractRawText({ arrayBuffer: bytes.buffer });
        return result.value;
      }

      return data.content;
    } catch (err) {
      console.error("Error importing Dropbox file:", err);
      throw err;
    }
  }, []);

  const navigateUp = useCallback(() => {
    if (!currentPath) return;
    const parentPath = currentPath.split("/").slice(0, -1).join("/");
    listFiles(parentPath);
  }, [currentPath, listFiles]);

  return { files, loading, error, currentPath, listFiles, importFile, navigateUp };
}
